import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getVideoUrl, 
  getVideoUploadStatus, 
  getVideoStreamBlob,
  startVideo,
  updateProgress,
  completeVideo,
  calculateProgressPercent,
  shouldCompleteVideo
} from './CoursesServices';

const useVideoPlayer = (video, { onComplete } = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [streamBlobUrl, setStreamBlobUrl] = useState(null); // NOVO: blob URL para streaming
  
  // Estados para tracking de progresso
  const [isVideoStarted, setIsVideoStarted] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [totalWatchTime, setTotalWatchTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  
  // Refs para controle de intervalos e tracking
  const progressInterval = useRef(null);
  const playerRef = useRef(null);
  const lastProgressRef = useRef(0);
  const startTimeRef = useRef(0);

  // Para compatibilidade, manter o videoId como referência
  const videoId = video?.id;

  // Verificar status de upload (apenas se necessário)
  const checkUploadStatus = useCallback(async () => {
    if (!videoId) return;

    try {
      const result = await getVideoUploadStatus(videoId);
      
      if (result.success) {
        setUploadStatus(result.data);
        return result.data;
      } else {
        // Erro ao verificar status de upload
        return null;
      }
    } catch (err) {
      // Erro inesperado ao verificar upload
      return null;
    }
  }, [videoId]);

  // Obter URL do vídeo (ATUALIZADO para suportar streaming)
  const getStreamUrl = (video) => {
    
    const result = getVideoUrl(video);
    
    
    // Verificar se precisa de streaming
    if (result?.startsWith('STREAMING_REQUIRED:')) {
      
      if (streamBlobUrl) {
        return streamBlobUrl;
      } else {
        loadStreamBlob(video);
        return null;
      }
    }
    
    // URL externa normal
    return result;
  };
  
  // NOVO: Carregar blob para streaming
  const loadStreamBlob = async (video) => {
    if (!video?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      
      const result = await getVideoStreamBlob(video.id);
      
      if (result.success) {
        setStreamBlobUrl(result.blobUrl);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verificar se vídeo está disponível para reprodução
  const isVideoAvailable = useCallback(() => {
    
    if (!video) {
      return false;
    }
    
    
    let available = false;
    let reason = "";
    
    // Para vídeos externos, sempre disponível se tem URL (external_url ou video_url)
    if (video.video_type === 'external') {
      const externalUrl = video.external_url || video.video_url;
      available = !!externalUrl;
      reason = available ? "External URL presente" : "External URL ausente";
    }
    // Para vídeos internos (S3), verificar se há s3_url ou upload completado
    else if (video.video_type === 'internal') {
      const hasS3Url = !!video.s3_url;
      const uploadCompleted = uploadStatus?.status === 'completed';
      available = hasS3Url || uploadCompleted;
      reason = hasS3Url ? "S3 URL presente" : uploadCompleted ? "Upload completado" : "S3 URL ausente e upload não completado";
    }
    // Para vídeos locais, verificar video_url (CORRIGIDO)
    else if (video.video_type === 'local') {
      available = !!video.video_url;
      reason = available ? "Video URL (local) presente" : "Video URL (local) ausente";
    }
    // Fallback
    else {
      available = !!video.s3_url || !!video.external_url || !!video.video_url;
      reason = "Fallback - verificando s3_url, external_url ou video_url";
    }
    
    
    return available;
  }, [video, uploadStatus]);

  // Verificar status de upload apenas para vídeos internos sem s3_url
  useEffect(() => {
    if (video?.video_type === 'internal' && !video.s3_url) {
      setLoading(true);
      checkUploadStatus().then(() => {
        setLoading(false);
      });
    }
  }, [video, checkUploadStatus]);
  
  // ===============================================
  // FUNÇÕES DE TRACKING DE PROGRESSO
  // ===============================================

  // Iniciar o vídeo na API
  const handleStartVideo = useCallback(async () => {
    if (!video?.id || isVideoStarted) return;
    
    
    try {
      const result = await startVideo(video.id);
      if (result.success) {
        setIsVideoStarted(true);
        startTimeRef.current = Date.now();
        
        // Inicializar progresso baseado nos dados retornados
        if (result.data.progress) {
          setCurrentProgress(result.data.progress.progress_percent || 0);
          setLastUpdateTime(result.data.progress.last_position_seconds || 0);
          setTotalWatchTime(result.data.progress.total_watch_time || 0);
          setIsCompleted(result.data.progress.completed || false);
          lastProgressRef.current = result.data.progress.progress_percent || 0;
        }
        
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [video?.id, isVideoStarted]);

  // Atualizar progresso na API
  const handleUpdateProgress = useCallback(async (player) => {
    if (!video?.id || !isVideoStarted || !player || isCompleted) {
      return;
    }
    
    try {
      // CORRIGIDO: ReactPlayer tem métodos específicos para obter tempo e duração
      let currentTime = 0;
      let duration = 0;
      
      // Tentar diferentes formas de obter tempo atual
      if (player.getCurrentTime) {
        currentTime = player.getCurrentTime();
      } else if (player.getInternalPlayer) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer?.getCurrentTime) {
          currentTime = internalPlayer.getCurrentTime();
        } else if (internalPlayer?.currentTime) {
          currentTime = internalPlayer.currentTime;
        }
      }
      
      // Tentar diferentes formas de obter duração
      if (player.getDuration) {
        duration = player.getDuration();
      } else if (player.getInternalPlayer) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer?.getDuration) {
          duration = internalPlayer.getDuration();
        } else if (internalPlayer?.duration) {
          duration = internalPlayer.duration;
        }
      }
      
      if (!duration || duration <= 0) {
        return;
      }
      
      // Calcular progresso atual
      const progressPercent = calculateProgressPercent(currentTime, duration);
      const watchTimeIncrement = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const newTotalWatchTime = totalWatchTime + Math.max(0, watchTimeIncrement);
      
      // CORRIGIDO: Atualizar sempre a cada 10 segundos, não só quando há mudança de 1%
      // Isso garante que o progresso seja salvo regularmente
      
      const result = await updateProgress(
        video.id,
        progressPercent,
        Math.floor(currentTime),
        newTotalWatchTime
      );
      
      if (result.success) {
        setCurrentProgress(progressPercent);
        setLastUpdateTime(Math.floor(currentTime));
        setTotalWatchTime(newTotalWatchTime);
        lastProgressRef.current = progressPercent;
        startTimeRef.current = Date.now(); // Reset timer
        
        // Verificar se deve marcar como completo
        const completionThreshold = result.data?.completion_threshold || 80.0;
        
        if (shouldCompleteVideo(progressPercent, completionThreshold) && !isCompleted) {
          await handleCompleteVideo();
        }
        
      } else {
      }
    } catch (err) {
    }
  }, [video?.id, isVideoStarted, isCompleted, totalWatchTime]);

  // Completar vídeo na API
  const handleCompleteVideo = useCallback(async () => {
    if (!video?.id || isCompleted) return;
    
    
    try {
      const result = await completeVideo(video.id);
      if (result.success) {
        setIsCompleted(true);
        setCurrentProgress(100);
        
        // Notificar componente pai via callback
        onComplete?.(video.id, result.data);
        
      } else {
      }
    } catch (err) {
    }
  }, [video?.id, isCompleted]);

  // Iniciar tracking automático a cada 10 segundos
  const startProgressTracking = useCallback((player) => {
    if (progressInterval.current || !player) return;
    
    
    progressInterval.current = setInterval(() => {
      handleUpdateProgress(player);
    }, 10000); // 10 segundos
    
  }, [handleUpdateProgress]);

  // Parar tracking automático
  const stopProgressTracking = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  }, []);

  // Função pública para conectar com ReactPlayer
  const connectPlayer = useCallback((player) => {
    playerRef.current = player;
    
    // CORRIGIDO: Sempre iniciar vídeo na primeira conexão
    handleStartVideo();
    
    // Iniciar tracking automático
    startProgressTracking(player);
  }, [handleStartVideo, startProgressTracking]);

  // Função pública para desconectar player
  const disconnectPlayer = useCallback(() => {
    stopProgressTracking();
    playerRef.current = null;
  }, [stopProgressTracking]);

  // ===============================================
  // EFFECTS DE LIMPEZA E INICIALIZAÇÃO
  // ===============================================

  // Limpar tracking quando vídeo mudar
  useEffect(() => {
    return () => {
      stopProgressTracking();
      if (streamBlobUrl) {
        URL.revokeObjectURL(streamBlobUrl);
        setStreamBlobUrl(null);
      }
      
      // Reset estados de progresso
      setIsVideoStarted(false);
      setCurrentProgress(0);
      setLastUpdateTime(0);
      setTotalWatchTime(0);
      setIsCompleted(false);
      lastProgressRef.current = 0;
      startTimeRef.current = 0;
    };
  }, [video?.id, stopProgressTracking]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // NOVO: Limpar blob URL quando vídeo mudar
  useEffect(() => {
    return () => {
      if (streamBlobUrl) {
        URL.revokeObjectURL(streamBlobUrl);
        setStreamBlobUrl(null);
      }
    };
  }, [video?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug final do hook
  const hookResult = {
    loading,
    error,
    uploadStatus,
    checkUploadStatus,
    getStreamUrl,
    isVideoAvailable: isVideoAvailable(),
    // Métodos utilitários
    hasError: !!error,
    isExternal: video?.video_type === 'external',
    isInternal: video?.video_type === 'internal',
    isLocal: video?.video_type === 'local', // NOVO: detector de vídeo local
    
    // ===============================================
    // NOVOS: Funcionalidades de progresso
    // ===============================================
    
    // Estados de progresso
    isVideoStarted,
    currentProgress,
    lastUpdateTime,
    totalWatchTime,
    isCompleted,
    
    // Métodos de controle
    connectPlayer,
    disconnectPlayer,
    handleStartVideo,
    handleUpdateProgress: (player) => handleUpdateProgress(player),
    handleCompleteVideo,
    
    // Métodos utilitários de progresso
    startProgressTracking,
    stopProgressTracking,
    
    // Info de progresso formatada
    progressInfo: {
      percent: Math.round(currentProgress * 100) / 100,
      timeWatched: totalWatchTime,
      lastPosition: lastUpdateTime,
      isComplete: isCompleted,
      isStarted: isVideoStarted
    }
  };
  
  
  return hookResult;
};

export default useVideoPlayer;